import cx from 'classnames'
import {
  OverlayArrow as AriaOverlayArrow,
  Popover as AriaPopover,
} from 'react-aria-components'
import { RawButton } from '@/components/Button'
import { Badge } from '@/components/Badge'
import { Tooltip, TooltipTrigger } from '@/components/Tooltip'
import { Icon } from '@/components/Icon'
import { m, useReducedMotion, type Variants } from 'motion/react'
import type { Transition } from 'motion-dom'
import {
  type ReactNode,
  type Ref,
  type RefObject,
  useRef,
  useState,
} from 'react'
import styles from './ChatIconButton.module.css'
import flattenChildren from 'react-keyed-flatten-children'
import { useModalAnimationStateControls } from '@/contexts/animation-state-context'
import { usePress } from 'react-aria'
import { ScreenHeaderTopBarReactAriaPortalProvider } from '@/components/ScreenHeader'

export interface ChatIconButtonProps {
  onPress?: () => unknown
  label: string
  badgeCount?: number
  isDisabled?: boolean
  avatarUrl?: string
  layout?: boolean
  layoutId?: string
  transition?: Transition
  testId?: string

  /**
   * If specified, shows a message in a popover, grabbing the user's attention.
   */
  popoverMessage?: string | null
}

/**
 * Motion-enabled raw button.
 */
const MotionRawButton = m.create(RawButton)

/**
 * An icon button used to indicate that a chat can be opened.
 */
export function ChatIconButton({
  onPress,
  isDisabled,
  label,
  avatarUrl,
  badgeCount = 0,
  layoutId,
  layout,
  transition,
  popoverMessage,
  testId,
}: ChatIconButtonProps) {
  // Track if we're animating the button to avoid showing the popover message.
  // This is needed because when the animation starts, thats where the popover
  // will be positioned, rather than where it ends. To get around this, we'll
  // only show the popover message while not animating.
  const [isAnimating, setIsAnimating] = useState(false)
  const triggerRef = useRef<Element>(null)

  return (
    <TooltipTrigger>
      <MotionRawButton
        transition={transition}
        layout={layout && 'position'}
        layoutId={layoutId}
        isDisabled={isDisabled}
        onPress={onPress}
        testId={testId}
        ref={triggerRef as Ref<HTMLButtonElement>}
        onLayoutAnimationStart={() => setIsAnimating(true)}
        onLayoutAnimationComplete={() => setIsAnimating(false)}
        className={({ isHovered, isPressed, isDisabled, isFocusVisible }) =>
          cx(
            styles.root,
            isHovered && styles.hover,
            isPressed && styles.pressed,
            isDisabled && styles.disabled,
            isFocusVisible && styles.focusVisible,
          )
        }
      >
        <div className={styles.badge}>
          <Badge hidden={badgeCount === 0}>{badgeCount}</Badge>
        </div>
        {avatarUrl ? (
          <img src={avatarUrl} className={styles.image} alt="Avatar" />
        ) : (
          <div className={styles.defaultIcon}>
            <Icon variant="active" name="support" />
          </div>
        )}
      </MotionRawButton>

      <ChatIconMessageBubblePopover
        triggerRef={triggerRef}
        onPress={onPress}
        isDisabled={isDisabled}
      >
        {!isAnimating && popoverMessage}
      </ChatIconMessageBubblePopover>

      <Tooltip placement="bottom">{label}</Tooltip>
    </TooltipTrigger>
  )
}

const MotionPopover = m.create(AriaPopover)

const PopoverVariantNames = {
  VISIBLE: 'chat-icon-popover-visible',
  HIDDEN: 'chat-icon-popover-hidden',
}

const popoverVariants: Variants = {
  [PopoverVariantNames.VISIBLE]: {
    '--animation-offset': '0px',
    opacity: 1,
  },
  [PopoverVariantNames.HIDDEN]: {
    '--animation-offset': '4px',
    opacity: 0,
  },
}

const popoverVariantsReducedMotion: Variants = {
  [PopoverVariantNames.VISIBLE]: {
    '--animation-offset': '0px',
    opacity: 1,
  },
  [PopoverVariantNames.HIDDEN]: {
    '--animation-offset': '0px',
    opacity: 0,
  },
}

interface ChatIconNewMessagePopoverProps {
  children?: ReactNode
  triggerRef: RefObject<Element | null>
  isDisabled?: boolean
  onPress?: () => unknown
}

/**
 * Popover showing a message bubble.
 */
function ChatIconMessageBubblePopover({
  children,
  triggerRef,
  onPress,
  isDisabled,
}: ChatIconNewMessagePopoverProps) {
  const shouldReduceMotion = useReducedMotion()
  const isOpen = flattenChildren(children).length !== 0
  const animationStateProps = useModalAnimationStateControls({
    isOpen,
    visibleVariant: PopoverVariantNames.VISIBLE,
    hiddenVariant: PopoverVariantNames.HIDDEN,
  })

  // Cache children so we don't render an empty popover while animating out.
  // Note: Mutating refs during render violates React Compiler purity rules,
  // but is safe here because both write and read occur in the same render
  // pass, and the ref only affects visual content (not reconciliation).
  // Worst case in concurrent rendering: slightly stale content during exit.
  const prevChildren = useRef(children)
  if (isOpen) {
    // eslint-disable-next-line react-compiler/react-compiler
    prevChildren.current = children
  }

  const { pressProps } = usePress({
    onPress,
    isDisabled,
  })

  return (
    <ScreenHeaderTopBarReactAriaPortalProvider>
      <MotionPopover
        triggerRef={triggerRef}
        isOpen={isOpen}
        isNonModal
        className={cx(
          styles.messageBubblePopover,
          isDisabled && styles.disabled,
        )}
        placement="bottom right"
        variants={
          shouldReduceMotion ? popoverVariantsReducedMotion : popoverVariants
        }
        transition={{
          duration: 0.25,
        }}
        {
          // SAFETY: press props don't specify any of the incompatible motion
          // handlers props.
          /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
          ...(pressProps as any)
        }
        {...animationStateProps}
      >
        <AriaOverlayArrow className={styles.arrow}>
          <svg width={12} height={12} viewBox="0 0 12 12">
            <path d="M0 0 L6 6 L12 0" />
          </svg>
        </AriaOverlayArrow>

        {children ||
          /* eslint-disable-next-line react-compiler/react-compiler */
          prevChildren.current}
      </MotionPopover>
    </ScreenHeaderTopBarReactAriaPortalProvider>
  )
}
